// src/pages/login.tsx
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';

import useClassnames from 'hook/use-classnames';
import cancelToken from 'component/core/cancel-token';
import { auth } from 'component/api/auth';

import UI from 'component/ui';
import Button from 'component/button';
import Error from 'component/error';

import Form, { createRegistry } from 'component/form';
import { IPayload, IError } from 'component/form/types';
import FormInput from 'component/form/input';

import { IStore } from 'src/types/reducers';
import { key as keyUser } from 'component/user/reducer';
import { set, reset } from 'component/user/actions';
import { login } from 'component/api/user';
import { ErrorCode } from 'component/api/types/api/account/login/post/code-400';

import style from './style.pcss';

const Login = () => {
    const cn = useClassnames(style);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const registry = useMemo(createRegistry, []);
    const cancelTokenLogin = useMemo(cancelToken.create, []);

    const isAuth = useSelector<IStore, boolean>((store) => !!store[keyUser].id);

    const [pending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<ErrorCode | null>(null);
    const [errors, setErrors] = useState<Array<IError> | null>(null);

    useEffect(() => {
        return () => {
            cancelTokenLogin.remove();
        };
    }, []);

    const onSubmit = useCallback(
        (formData: IPayload) => {
            setPending(true);
            setError(null);
            setErrors(null);
            setErrorCode(null);

            login({
                cancelToken: cancelTokenLogin.new(),
                data: formData
            })
                .then((resp) => {
                    auth.setTokens(resp.access, resp.refresh);
                    dispatch(set({ id: 1, username: formData.username }));
                    navigate('/antifrod');
                })
                .catch((payload) => {
                    if (!axios.isCancel(payload)) {
                        setError(payload.message);
                        setErrorCode(payload.error_code);
                        if (payload.errors) { setErrors(payload.errors); }
                        dispatch(reset());
                    }
                })
                .finally(() => {
                    setPending(false);
                });
        },
        [dispatch, navigate]
    );

    const elError = useMemo(() => {
        if (error) {
            return <Error className={cn('login__error')}>{error}</Error>;
        }
    }, [error]);

    if (isAuth) {
        return <Navigate to="/antifrod" replace={true} />;
    }

    return (
        <UI.Main className={cn('login__ui-main')}>
            <UI.Box padding={true} className={cn('login__ui-box')}>
                <div className={cn('login__content')}>
                    <h2 className={cn('login__header')}>Сервис проверки чеков по видео</h2>
                    <Form
                        registry={registry.form}
                        className={cn('login__form')}
                        onSubmit={onSubmit}
                        errors={errors}
                    >
                        <FormInput
                            registry={registry.field}
                            name="username"
                            type="text"
                            className={cn('login__input')}
                            direction="column"
                            required={true}
                            autoFocus={true}
                            tabIndex={1}
                        >
                            Логин
                        </FormInput>
                        <FormInput
                            registry={registry.field}
                            name="password"
                            type="password"
                            className={cn('login__input')}
                            direction="column"
                            required={true}
                            tabIndex={2}
                        >
                            Пароль
                        </FormInput>
                        {elError}
                        <Button
                            type="submit"
                            className={cn('login__button-submit')}
                            isLoading={pending}
                            disabled={pending}
                        >
                            Войти
                        </Button>
                    </Form>
                </div>
            </UI.Box>
        </UI.Main>
    );
};

export default Login;
